/**
 * ComparePage.tsx — /compare — ɳSentry vs competitors comparison page
 *
 * Purpose:    Detailed comparison of ɳSentry vs Sentry.io, Datadog with feature + privacy table
 * Inputs:     Static COMPARISON data
 * Outputs:    Comparison page with technical details and CTA
 * Note:       Lived at /privacy historically (misnamed); /privacy now redirects
 *             to the real privacy policy at /legal/privacy and this content
 *             lives at /compare.
 * Constraints: React Router Link for internal navigation
 * SOT:        T-P3-E3-W2-S5-T01 · nsentry SaaS completion spec § G-WEB (legal)
 */
import { Link } from 'react-router-dom'

const COMPARISON: Array<{
  cap: string
  nsentry: string
  sentry: string
  datadog: string
}> = [
  { cap: 'Who stores your data', nsentry: 'Your server', sentry: 'Sentry.io servers', datadog: 'Datadog servers' },
  { cap: 'Starting price', nsentry: '$0.99/mo (bundle)', sentry: '$26+/mo', datadog: '$15+/host/mo' },
  { cap: 'Status pages', nsentry: 'Included (public + private)', sentry: 'Add-on / separate product', datadog: 'Add-on / separate product' },
  { cap: 'Incident management', nsentry: 'Included', sentry: 'Included', datadog: 'Add-on' },
  { cap: 'SLO tracking', nsentry: 'Included', sentry: 'Paid tiers only', datadog: 'Paid tiers only' },
  { cap: 'Real-user monitoring', nsentry: 'Included', sentry: 'Separate pricing', datadog: 'Separate pricing' },
  { cap: 'Uptime monitoring', nsentry: 'Included', sentry: 'Crons only', datadog: 'Included (paid)' },
  { cap: 'Data leaves your server', nsentry: 'Never', sentry: 'Yes (SaaS)', datadog: 'Yes (SaaS)' },
  { cap: 'Per-event pricing', nsentry: 'No', sentry: 'Yes (overage fees)', datadog: 'Yes (per host/metric)' },
  { cap: 'Infra monitoring', nsentry: 'Free (Prometheus/Grafana)', sentry: 'Not included', datadog: 'Core product' },
  { cap: 'Audit log', nsentry: 'Free in all tiers', sentry: 'Enterprise tier', datadog: 'Enterprise tier' },
  { cap: 'Business model', nsentry: 'Bundle subscription ($0.99/mo)', sentry: 'SaaS subscription + overages', datadog: 'Per-host/metric SaaS' },
]

export function ComparePage() {
  return (
    <>
      {/* Hero */}
      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8 text-center">
        <h1 className="mb-4 text-4xl font-bold sm:text-5xl">ɳSentry vs Sentry.io</h1>
        <p className="text-lg text-brand-muted">
          Same observability features. Your server. $0.99/mo instead of $26+/mo.
          No per-event pricing. No data leaving your infrastructure.
        </p>
      </section>

      {/* Technical details */}
      <section className="border-t border-brand-subtle bg-brand-surface/30 py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-8 text-2xl font-bold">What self-hosted observability means</h2>
          <div className="grid gap-6 sm:grid-cols-2">
            {[
              {
                title: 'Your Postgres database',
                body: 'All observability data — error events, uptime results, incident records, RUM sessions — lives in a Postgres database running on your VPS. We have no connection to it.',
              },
              {
                title: 'Free infra monitoring already included',
                body: 'Every ɳSelf instance ships with Prometheus, Grafana, Loki, and the full monitoring stack at no cost. ɳSentry adds the product-layer on top.',
              },
              {
                title: 'Your domain, your SSL',
                body: "ɳSelf handles Let's Encrypt certificates automatically. Your status pages live on a domain you own, not a subdomain of ours.",
              },
              {
                title: 'No per-event overages',
                body: 'ɳSentry is priced per ɳSelf instance, not per error or per event. Ingest as many events as your server can handle. No surprise bills.',
              },
            ].map((item) => (
              <div key={item.title} className="rounded-xl border border-brand-subtle bg-brand-bg p-6">
                <h3 className="mb-2 font-semibold text-brand-ink">{item.title}</h3>
                <p className="text-sm text-brand-muted">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-8 text-2xl font-bold text-center">ɳSentry vs the alternatives</h2>
          <div className="overflow-x-auto rounded-xl border border-brand-subtle">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-subtle bg-brand-surface">
                  <th className="px-4 py-3 text-left font-medium text-brand-muted">What to compare</th>
                  <th className="px-4 py-3 text-center font-semibold text-indigo-700 dark:text-indigo-400">ɳSentry</th>
                  <th className="px-4 py-3 text-center font-medium text-brand-muted">Sentry.io</th>
                  <th className="px-4 py-3 text-center font-medium text-brand-muted">Datadog</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.cap} className={i % 2 === 0 ? 'bg-brand-bg' : 'bg-brand-surface/30'}>
                    <td className="px-4 py-3 font-medium text-brand-secondary">{row.cap}</td>
                    <td className="px-4 py-3 text-center text-indigo-700 dark:text-indigo-300">{row.nsentry}</td>
                    <td className="px-4 py-3 text-center text-brand-muted">{row.sentry}</td>
                    <td className="px-4 py-3 text-center text-brand-muted">{row.datadog}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-brand-subtle py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="mb-4 text-2xl font-bold">Ready to switch?</h2>
          <p className="mb-8 text-brand-muted">
            Join the waitlist. When ɳSentry ships in v1.1.0 you will get an invite code. $0.99/mo, or free
            with ɳSelf+ ($3.99/mo).
          </p>
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link
              to="/invite"
              className="rounded-md bg-sky-500 px-8 py-3 text-base font-medium text-white shadow-lg hover:bg-sky-400 transition-colors"
            >
              Join the waitlist
            </Link>
            <Link
              to="/self-host"
              className="text-sm text-brand-muted hover:text-brand-ink transition-colors"
            >
              Self-host guide &rarr;
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
